/**
 * Copyright 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useCallback, useMemo } from "react";
import {
  useEnvironmentOtherCategoriesCostQuery,
  useEnvironmentTopCategoriesCostsQuery,
} from "~/generated/graphql";
import {
  extractCategoryIds,
  getTableGranularity,
  mapGranularity,
  Granularity,
  Period,
  PlaceholderCategory,
  TableData,
  topSpendingGroup,
  generateTopGroupData,
  generateTopTableData,
} from "~/components";
import { isNotNil } from "~/tools";
import { getCostSplitGroupNames } from "~/views/applications/ApplicationCostTabs/hooks/utils";

type Props = {
  environmentId: string;
  period: Period;
  startDate: string;
  endDate: string;
  granularity: Granularity;
};

export function useEnvCostPerCategoriesChart({
  environmentId,
  period,
  startDate,
  endDate,
  granularity,
}: Props) {
  const {
    data,
    loading: isCostLoading,
    error: costError,
  } = useEnvironmentTopCategoriesCostsQuery({
    variables: {
      top: topSpendingGroup.CATEGORIES,
      environmentId,
      startDate,
      endDate,
      granularity: mapGranularity(granularity),
      tableGranularity: getTableGranularity(granularity, period),
    },
    // No-cache policy is enabled in all cases because networking responses are different than console,
    // Some identifiers may be the same for apollo: https://github.com/apollographql/apollo-client/issues/8359
    // TODO: solve discrepancies issue and remove "no-cache", JIRA ticket: https://nordcloud.atlassian.net/browse/KLAR-7698
    fetchPolicy: "no-cache",
  });

  const {
    data: environmentOtherCategoriesCostTableResponse,
    loading: isOtherCategoriesLoading,
    error: otherCategoriesError,
  } = useEnvironmentOtherCategoriesCostQuery({
    variables: {
      input: {
        top: topSpendingGroup.CATEGORIES,
        environmentId,
        startDate,
        endDate,
        granularity: mapGranularity(Granularity.MONTHS),
      },
    },
  });

  const environmentTopCategoriesCostTableTimePoints = useMemo(
    () =>
      (data?.environmentTopCategoriesTableCost.timePoints ?? []).filter(
        isNotNil
      ),
    [data]
  );

  const costPerCategoryTableData: TableData[] = useMemo(
    () =>
      generateTopGroupData(environmentTopCategoriesCostTableTimePoints ?? []),
    [environmentTopCategoriesCostTableTimePoints]
  );

  const environmentTopCategoriesCostChartData = useMemo(
    () =>
      (data?.environmentTopCategoriesCost?.timePoints ?? []).filter(isNotNil),
    [data]
  );

  const costPerCategoryTotal = Number(
    data?.environmentTopCategoriesTableCost?.total ?? "0"
  );

  const environmentOtherCategoriesCostTableData = (
    environmentOtherCategoriesCostTableResponse?.environmentOtherCategoriesCost
      ?.timePoints ?? []
  ).filter(isNotNil);

  const costPerOtherCategoryTableData: TableData[] = useMemo(
    () => generateTopTableData(environmentOtherCategoriesCostTableData ?? []),
    [environmentOtherCategoriesCostTableData]
  );

  const getSubRows = useCallback(
    (originalRow: TableData): TableData[] =>
      originalRow.field === PlaceholderCategory.OTHER.name
        ? costPerOtherCategoryTableData
        : [],
    [costPerOtherCategoryTableData]
  );

  const environments = getCostSplitGroupNames(costPerCategoryTableData);

  const otherCategoryIds = extractCategoryIds(
    environmentOtherCategoriesCostTableData
  );

  const isLoading = isCostLoading || isOtherCategoriesLoading;
  const error = costError || otherCategoriesError;

  return {
    environmentTopCategoriesCostChartData,
    costPerCategoryTableData,
    costPerCategoryTotal,
    loading: isLoading,
    error,
    getSubRows,
    environments,
    otherCategoryIds,
  };
}
